import { z } from 'zod';
import { DimensionDefinition } from 'libs/end-to-end/src/types/databases/dimensions/dimension-definition';
import { FocusAreaDefinition } from 'libs/end-to-end/src/types/databases/focus-areas/focus-area-definition';
import { DatabaseRevisionSchemaValidationByMetricTrendsError } from 'libs/end-to-end/src/types/databases/revisions/database-revision-schema-validation-result';
import { DatabaseRevisionSchemaValidationCopilotAutoMLConfigurationError } from 'libs/end-to-end/src/types/databases/revisions/database-revision-schema-validation-result';
import { DatabaseRevisionSchemaValidationCopilotError } from 'libs/end-to-end/src/types/databases/revisions/database-revision-schema-validation-result';
import { DatabaseRevisionSchemaValidationMiscError } from 'libs/end-to-end/src/types/databases/revisions/database-revision-schema-validation-result';
import { DatabaseRevisionSchemaValidationUpsellError } from 'libs/end-to-end/src/types/databases/revisions/database-revision-schema-validation-result';
import { DatabaseRevisionSchemaValidationDimensionInsights } from 'libs/end-to-end/src/types/databases/revisions/database-revision-schema-validation-result';
import { DatabaseRevisionSchemaValidationFilterError } from 'libs/end-to-end/src/types/databases/revisions/database-revision-schema-validation-result';
import { DatabaseRevisionSchemaValidationFocusAreaError } from 'libs/end-to-end/src/types/databases/revisions/database-revision-schema-validation-result';
import { FullCopilotDefinition } from 'libs/end-to-end/src/types/databases/copilots/copilot-definition';
import { UpsellDefinition } from 'libs/end-to-end/src/types/databases/copilots/upsell-definition';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { FilterSetDefinitions } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { TransformInsightsDateColumn } from 'libs/end-to-end/src/types/databases/transform/transform-insights';
import { TransformInsightsNumericColumn } from 'libs/end-to-end/src/types/databases/transform/transform-insights';
import { TransformUniqueValuesByColumnItem } from 'libs/end-to-end/src/types/databases/transform/transform-insights';
import { HiringNotificationEmailSubjectSuffixTemplate } from 'libs/end-to-end/src/types/hiring-notification-sender/hiring-notification-email-subject-suffix-template';
import { DynamicPipelineAnalysisConfiguration } from 'libs/end-to-end/src/dynamic-pipeline-analysis-configuration';
import { ByMetricTrendsConfig } from 'libs/end-to-end/src/types/databases/trends/by-metric-trends-config';
import { CopilotAutoMLConfiguration } from 'libs/end-to-end/src/copilot';

export const PossibleDimensionSelection = z.object({
  name: z.string(),
  distinctValues: z.array(z.string()),
});

export type PossibleDimensionSelection = z.infer<
  typeof PossibleDimensionSelection
>;

export const DatabaseRevisionSchemaOptions = z.object({
  possibleDimensionSelections: z.array(PossibleDimensionSelection),
  possibleMetricSelections: z.array(z.string()),
  possibleControlVariableSelections: z.array(z.string()),
  possibleAnySelections: z.array(z.string()), // this contains a list of all columns, so it can be useful for the high level validation
  dimensionDefinitions: z.array(DimensionDefinition),
  copilotDefinitions: z.array(FullCopilotDefinition),
  focusAreaDefinitions: z.array(FocusAreaDefinition),
  upsellDefinitions: z.array(UpsellDefinition),
  filterSets: FilterSetDefinitions,
  uniqueValuesByColumn: z.array(TransformUniqueValuesByColumnItem),
  numericColumns: z
    .record(z.string(), TransformInsightsNumericColumn)
    .optional(),
  dateColumns: z.record(z.string(), TransformInsightsDateColumn).optional(),
  validations: z.object({
    dimensions: z.array(DatabaseRevisionSchemaValidationDimensionInsights),
    focusAreas: z.array(DatabaseRevisionSchemaValidationFocusAreaError),
    filters: z.array(DatabaseRevisionSchemaValidationFilterError),
    copilots: z.array(DatabaseRevisionSchemaValidationCopilotError),
    copilotAutoMLConfiguration: z.array(
      DatabaseRevisionSchemaValidationCopilotAutoMLConfigurationError,
    ),
    upsells: z.array(DatabaseRevisionSchemaValidationUpsellError),
    miscs: z.array(DatabaseRevisionSchemaValidationMiscError),
    byMetricTrends: z.array(
      DatabaseRevisionSchemaValidationByMetricTrendsError,
    ),
  }),
  calibrationFraming: CalibrationFraming,
  showAllInsights: z.boolean(),
  showKeyFactors: z.boolean().optional(),
  showGeolocation: z.boolean().optional(),
  showNewDataIntelligenceDeepDivePage: z.boolean(),
  showProgressPage: z.boolean(),
  showSavedAnalysesPage: z.boolean(),
  showRubricsPage: z.boolean(),
  showDownloadMultiAnalysisButton: z.boolean(),
  showDownloadSingleAnalysisButton: z.boolean(),
  showDynamicPipelineAnalysisPage: z.boolean(),
  showExplorePage: z.boolean(),
  showDashboardsPage: z.boolean(),
  showHeatmapAverages: z.boolean(),
  showCustomChartTypes: z.boolean(),
  showFobButtonInDashboardPage: z.boolean(),
  sendHiringNotificationSummaryEmailAfterTopLevelAnalysis: z
    .boolean()
    .optional(),
  hiringNotificationEmailSubjectSuffixTemplate:
    HiringNotificationEmailSubjectSuffixTemplate.optional(),
  forceHiringNotificationSummaryToOnlySendToThisEmail: z
    .string()
    .email()
    .optional(),
  numPreviousDaysOfApplicantsForHiringNotificationSummaryEmail: z
    .number()
    .min(1)
    .optional(),
  minReadinessRatingForHiringNotificationSummaryEmail: z
    .number()
    .min(1)
    .optional(),
  additionalEmailsToSendAllHiringNotificationSummaryEmailsTo: z
    .string()
    .email()
    .array()
    .optional(),
  enableAtsWriteBack: z.boolean().optional(),
  showAIExplanations: z.boolean(),
  hideFocusAreaSolutionsPage: z.boolean(),
  hideEmployeeLifecyclePage: z.boolean(),
  translationPhrasingCode: z.string(),
  overrideUploadSnapshotDate: z.string(),
  pipelineAnalysisConfiguration: z
    .array(DynamicPipelineAnalysisConfiguration)
    .optional(),
  useDisparityOnRay: z.boolean().optional(),
  useDisparityOnParquet: z.boolean().optional(),
  useHeatmapOnRay: z.boolean().optional(),
  useFAOnRay: z.boolean().optional(),
  byMetricTrendsConfiguration: ByMetricTrendsConfig,
  copilotAutoMLConfiguration: CopilotAutoMLConfiguration,
  showNewExplanations: z.boolean(),
  enablePublicDashboards: z.boolean(),
  showDashboardAISummaries: z.boolean(),
  showChatbot: z.boolean(),
});

export type DatabaseRevisionSchemaOptions = z.infer<
  typeof DatabaseRevisionSchemaOptions
>;
